// ADR-0009 Peça 6 — captura de liveness (vídeo + desafio server-side).
//
// 🔴 EXTRAÍDO do `web-comite` para `ui-primitives` em 2026-08-24, quando o `web-admin`
// passou a exigir reconhecimento facial no login. A alternativa era duplicar 414 linhas de
// um componente de AUTENTICAÇÃO — duas cópias que divergem a partir do primeiro dia, com o
// agravante de que a divergência só apareceria no login de alguém, em produção.
//
// O que tornou a extração barata: o acoplamento com o app era de TRÊS coisas apenas — duas
// chamadas de rede e um tipo. Elas viraram PROPS (`drawChallenge` / `verify`), então este
// arquivo não sabe qual app o hospeda nem qual endpoint responde. Cada app injeta o seu
// cliente, que é o que difere: o comitê fala por `/api/comite/liveness/*` com sessão de
// `comite_membro`, o admin com sessão de `admin_ti`.
//
// Fluxo (inalterado): sorteia desafio (servidor) → mostra "vire para X" → grava ~2.5s de
// VÍDEO (MediaRecorder) → verify → trata liberar/negar/locked/degraded/etc.
//
// Liveness é gate de auth DURO: sem câmera/permissão = hard-stop (contate TI).
// Não há skip a11y (decisão 2026-06-11) — coerente com "sem bypass".
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './Button';

/** Resultado do sorteio de desafio. Espelha o corpo de POST /liveness/challenge. */
export interface ChallengeResult {
  ok: boolean;
  error?: string;
  challengeToken?: string;
  direction?: 'direita' | 'esquerda';
  expiresInSeconds?: number;
}

/** Resultado tipado da verificação — dirige a UI. */
export type LivenessOutcome =
  | { kind: 'liberar' } // entrou (token elevado já aplicado pelo cliente do app)
  | { kind: 'negar'; reason: string; attemptsRemaining: number }
  | { kind: 'locked' } // 423 — bloqueado, contate TI
  | { kind: 'degraded' } // 503 — API indisponível, tente em instantes
  | { kind: 'invalid_capture' } // 422 — sem rosto/movimento no vídeo, regrave
  | { kind: 'challenge_invalid' } // 400 — desafio expirou, re-sortear
  | { kind: 'no_template' } // 422 — sem rosto cadastrado (TI)
  | { kind: 'error'; error: string };

type Direction = 'direita' | 'esquerda';

type Phase =
  | 'loading_challenge'
  | 'ready' // desafio sorteado, aguardando o usuário gravar
  | 'countdown' // câmera aberta, mostrando a direção + contagem antes de gravar
  | 'recording'
  | 'verifying'
  | 'retry' // negar / invalid_capture / degraded / erro recuperável → mostra msg + tentar de novo
  | 'locked' // terminal
  | 'no_camera'; // terminal

const MAX_ATTEMPTS = 3;
const VERIFY_ABORT_MS = 90_000; // > 70s do servidor (cold-start) + margem.
const COUNTDOWN_FROM = 3; // "gravando em 3… 2… 1…" antes de iniciar a gravação.

/** mimeType suportado para o MediaRecorder (varia por browser). */
function pickMimeType(): string | undefined {
  const candidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'];
  const MR = globalThis.MediaRecorder as typeof MediaRecorder | undefined;
  if (!MR || typeof MR.isTypeSupported !== 'function') return undefined;
  return candidates.find((c) => MR.isTypeSupported(c));
}

function dirLabel(d: Direction): string {
  return d === 'direita' ? 'DIREITA' : 'ESQUERDA';
}

function negarMessage(reason: string): string {
  switch (reason) {
    case 'sem_movimento':
      return 'Não detectamos movimento real da cabeça. Vire com clareza para o lado pedido.';
    case 'desafio_incorreto':
      return 'Você virou para o lado errado. Siga a direção indicada na tela.';
    case 'rosto_nao_corresponde':
      return 'O rosto não corresponde ao cadastro. Tente novamente.';
    default:
      return 'Verificação não passou. Tente novamente.';
  }
}

export function LivenessCapture({
  onVerified,
  drawChallenge,
  verify,
  recordMs = 2500,
  countdownStepMs = 700,
}: {
  /** Chamado quando a verificação libera (token elevado já aplicado pelo cliente do app). */
  onVerified: () => void;
  /** Cliente do app: POST /liveness/challenge. Injetado para o componente não conhecer rota. */
  drawChallenge: () => Promise<ChallengeResult>;
  /** Cliente do app: POST /liveness/verify (multipart). Mesma razão. */
  verify: (challengeToken: string, video: Blob, signal?: AbortSignal) => Promise<LivenessOutcome>;
  /** Duração da gravação em ms (default 2500). Param para testes determinísticos. */
  recordMs?: number;
  /** Intervalo de cada passo da contagem regressiva em ms (default 700). Param para testes. */
  countdownStepMs?: number;
}): JSX.Element {
  const [phase, setPhase] = useState<Phase>('loading_challenge');
  const [direction, setDirection] = useState<Direction | null>(null);
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0); // tentativas de laudo NEGATIVO (UX; servidor é a verdade)
  const [message, setMessage] = useState<string | null>(null);
  const [count, setCount] = useState(COUNTDOWN_FROM); // contagem visível na fase 'countdown'

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopCamera = useCallback((): void => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  }, []);

  // Sorteia (ou re-sorteia) o desafio e volta para 'ready'.
  const loadChallenge = useCallback(async (): Promise<void> => {
    setPhase('loading_challenge');
    setMessage(null);
    const r = await drawChallenge();
    if (!r.ok || !r.challengeToken || !r.direction) {
      setMessage('Não foi possível iniciar a verificação. Tente novamente.');
      setPhase('retry');
      return;
    }
    setChallengeToken(r.challengeToken);
    setDirection(r.direction);
    setPhase('ready');
    // `drawChallenge` entrou nas deps ao virar PROP (extração 24/08). Sem isto o callback
    // fecharia sobre a primeira identidade recebida — inofensivo quando o app passa uma
    // função de módulo, e um bug silencioso no dia em que passar uma inline.
  }, [drawChallenge]);

  useEffect(() => {
    void loadChallenge();
  }, [loadChallenge]);

  // Cleanup ao desmontar — para câmera, gravação e fetch pendente.
  useEffect(() => {
    return () => {
      stopCamera();
      if (countdownTimerRef.current) clearTimeout(countdownTimerRef.current);
      if (recordTimerRef.current) clearTimeout(recordTimerRef.current);
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        try {
          recorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      abortRef.current?.abort();
    };
  }, [stopCamera]);

  // Envia o vídeo gravado para o /verify e trata o resultado.
  const submitVideo = useCallback(
    async (video: Blob): Promise<void> => {
      if (!challengeToken) return;
      setPhase('verifying');
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const timer = setTimeout(() => ctrl.abort(), VERIFY_ABORT_MS);
      let outcome: LivenessOutcome;
      try {
        outcome = await verify(challengeToken, video, ctrl.signal);
      } finally {
        clearTimeout(timer);
        abortRef.current = null;
      }

      switch (outcome.kind) {
        case 'liberar':
          onVerified();
          return;
        case 'negar': {
          setAttempt((a) => a + 1);
          // Re-sorteia ANTES de setar a msg (loadChallenge limpa message no início).
          await loadChallenge();
          setMessage(negarMessage(outcome.reason));
          setPhase('retry');
          return;
        }
        case 'locked':
          setPhase('locked');
          return;
        case 'degraded':
          await loadChallenge();
          setMessage(
            'Serviço de verificação indisponível no momento. Aguarde alguns instantes e tente novamente.',
          );
          setPhase('retry');
          return;
        case 'invalid_capture':
          await loadChallenge();
          setMessage(
            'Não detectamos um rosto/movimento claro no vídeo. Regrave com o rosto centralizado.',
          );
          setPhase('retry');
          return;
        case 'challenge_invalid':
          // Desafio expirou — re-sorteia silenciosamente e segue (sem mensagem).
          await loadChallenge();
          return;
        case 'no_template':
          setMessage(
            'Seu rosto ainda não foi cadastrado. Procure a TI para o cadastro biométrico.',
          );
          setPhase('retry');
          return;
        case 'error':
        default:
          await loadChallenge();
          setMessage(
            outcome.kind === 'error' && outcome.error === 'timeout'
              ? 'A verificação demorou demais. Tente novamente.'
              : 'Erro de conexão na verificação. Tente novamente.',
          );
          setPhase('retry');
          return;
      }
    },
    // idem para `verify` — ver o comentário em `loadChallenge`.
    [challengeToken, onVerified, loadChallenge, verify],
  );

  // Abre a câmera e grava ~recordMs de vídeo, depois envia.
  const startRecording = useCallback(async (): Promise<void> => {
    setMessage(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
    } catch {
      setPhase('no_camera');
      return;
    }
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      try {
        await videoRef.current.play();
      } catch {
        /* autoplay pode falhar silenciosamente; segue */
      }
    }

    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      stopCamera();
      setMessage('Seu navegador não suporta a gravação necessária. Use um navegador atualizado.');
      setPhase('retry');
      return;
    }
    recorderRef.current = recorder;
    const chunks: BlobPart[] = [];
    recorder.ondataavailable = (e: BlobEvent): void => {
      if (e.data && e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = (): void => {
      stopCamera();
      const blob = new Blob(chunks, { type: mimeType ?? 'video/webm' });
      void submitVideo(blob);
    };

    // Inicia a gravação de fato (chamado ao fim da contagem regressiva).
    const beginRecording = (): void => {
      if (!recorderRef.current) return;
      setPhase('recording');
      recorderRef.current.start();
      recordTimerRef.current = setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
          recorderRef.current.stop();
        }
      }, recordMs);
    };

    // Contagem regressiva: a câmera já está aberta (preview) e a direção visível,
    // dando ao usuário um instante para se preparar antes de a gravação começar.
    setCount(COUNTDOWN_FROM);
    setPhase('countdown');
    const tick = (remaining: number): void => {
      if (remaining <= 1) {
        beginRecording();
        return;
      }
      setCount(remaining - 1);
      countdownTimerRef.current = setTimeout(() => tick(remaining - 1), countdownStepMs);
    };
    countdownTimerRef.current = setTimeout(() => tick(COUNTDOWN_FROM), countdownStepMs);
  }, [recordMs, countdownStepMs, stopCamera, submitVideo]);

  // ---------------- Render ----------------
  if (phase === 'locked') {
    return (
      <div className="card" data-testid="liveness-locked">
        <h2>Acesso bloqueado</h2>
        <div className="alert alert-error" role="alert">
          Você atingiu o limite de tentativas de verificação facial. Por segurança, o acesso foi
          bloqueado. <strong>Procure a TI</strong> para liberar (com registro e justificativa).
        </div>
      </div>
    );
  }

  if (phase === 'no_camera') {
    return (
      <div className="card" data-testid="liveness-no-camera">
        <h2>Câmera necessária</h2>
        <div className="alert alert-error" role="alert">
          O acesso do comitê exige verificação facial ao vivo, e a câmera não está disponível ou a
          permissão foi negada. Habilite a câmera no navegador e recarregue. Se houver impedimento,
          procure a TI.
        </div>
        <Button variant="ghost" block onClick={() => void loadChallenge()}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="card" data-testid="liveness-capture">
      <h2>Verificação facial</h2>
      <p style={{ color: 'var(--color-text-secondary)' }}>
        Para entrar, confirme que é você com um movimento ao vivo. Sua imagem é usada só para a
        verificação.
      </p>

      {message && (
        <div className="alert alert-error" role="alert" data-testid="liveness-message">
          {message}
          {attempt > 0 && phase === 'retry' && (
            <div style={{ marginTop: 4, fontSize: 13 }}>
              Tentativa {Math.min(attempt, MAX_ATTEMPTS)} de {MAX_ATTEMPTS}.
            </div>
          )}
        </div>
      )}

      {/* Desafio — direção autoritativa do servidor. Visível já no retry para o
          usuário saber a nova direção ANTES de clicar (a direção re-sorteia a cada
          tentativa) e durante a contagem/gravação. */}
      {direction &&
        (phase === 'ready' ||
          phase === 'retry' ||
          phase === 'countdown' ||
          phase === 'recording') && (
          <div className="alert alert-info" role="status" data-testid="liveness-challenge">
            {phase === 'recording' ? 'Agora, ' : 'Ao gravar, '}
            <strong>vire a cabeça para a {dirLabel(direction)}</strong>.
          </div>
        )}

      {/* Preview da câmera (visível durante a contagem e a gravação) */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 280,
          aspectRatio: '4 / 3',
          margin: '12px auto',
          display: phase === 'countdown' || phase === 'recording' ? 'block' : 'none',
        }}
      >
        <video
          ref={videoRef}
          playsInline
          autoPlay
          muted
          data-testid="liveness-video"
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 'var(--radius-md)',
            objectFit: 'cover',
            border: '2px solid var(--brand-sage)',
          }}
        />
        {phase === 'recording' && (
          <div
            style={{ position: 'absolute', top: 8, left: 8, color: '#d33', fontWeight: 700 }}
            aria-hidden="true"
          >
            ● Gravando
          </div>
        )}
        {phase === 'countdown' && (
          <div
            data-testid="liveness-countdown"
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.35)',
              borderRadius: 'var(--radius-md)',
              color: '#fff',
              fontSize: 72,
              fontWeight: 800,
              textShadow: '0 2px 12px rgba(0,0,0,0.6)',
            }}
          >
            {count}
          </div>
        )}
      </div>

      {phase === 'countdown' && (
        <div className="alert alert-info" role="status" data-testid="liveness-prep">
          Prepare-se… gravando em {count}.
        </div>
      )}

      {phase === 'loading_challenge' && (
        <div className="alert alert-info" role="status">
          <span className="spinner" /> Preparando a verificação…
        </div>
      )}

      {phase === 'verifying' && (
        <div className="alert alert-info" role="status" data-testid="liveness-verifying">
          <span className="spinner" /> Verificando… a primeira verificação pode levar até 1 minuto.
        </div>
      )}

      {(phase === 'ready' || phase === 'retry') && (
        <Button
          block
          data-testid="liveness-record-btn"
          onClick={() => void startRecording()}
          disabled={!challengeToken}
        >
          {phase === 'retry' ? 'Gravar novamente' : 'Permitir câmera e gravar'}
        </Button>
      )}
    </div>
  );
}
